// plugins/AtomicBoxPlugin/components/SortableList.jsx
/**
 * SortableList — pointer-event drag with LOCAL STATE + DOM-clone ghost.
 *
 * Ghost: cloneNode(true) + inline computed styles → looks identical to
 * the original card regardless of where the clone is placed in the DOM.
 *
 * _sortId handling: items can be strings (BulletList) or objects (CardGrid,
 * Timeline, Stats). We wrap each item in a { _item, _sortId } envelope
 * during drag, and unwrap when committing / rendering.
 */
import React, { useState, useRef, useEffect, useCallback } from "react";

/* eslint-disable react/prop-types */

function wrapItems(items) {
  return items.map((item, idx) => ({ _item: item, _sortId: `s${idx}` }));
}
function unwrapItems(envelopes) {
  return envelopes.map((env) => env._item);
}

function inlineComputedStyles(source, clone) {
  const computed = window.getComputedStyle(source);
  const props = [
    "font",
    "fontSize",
    "fontFamily",
    "fontWeight",
    "fontStyle",
    "color",
    "backgroundColor",
    "background",
    "padding",
    "margin",
    "border",
    "borderRadius",
    "display",
    "flexDirection",
    "gap",
    "alignItems",
    "justifyContent",
    "gridTemplateColumns",
    "gridTemplateRows",
    "textTransform",
    "letterSpacing",
    "lineHeight",
    "width",
    "height",
    "minWidth",
    "minHeight",
    "maxWidth",
    "maxHeight",
    "boxSizing",
    "textAlign",
    "whiteSpace",
    "overflow",
    "opacity",
  ];
  props.forEach((p) => {
    try {
      clone.style[p] = computed[p];
    } catch (_) {}
  });

  const srcChildren = source.children;
  const clnChildren = clone.children;
  for (let i = 0; i < srcChildren.length && i < clnChildren.length; i++) {
    inlineComputedStyles(srcChildren[i], clnChildren[i]);
  }
}

export function SortableList({
  editing,
  items,
  onReorder,
  renderItem,
  direction = "vertical",
  handleStyle = {},
}) {
  const [localEnvelopes, setLocalEnvelopes] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const itemRefs = useRef([]);
  const dragIdxRef = useRef(null);
  const localRef = useRef(null);
  const cleanupRef = useRef(null);
  const ghostElRef = useRef(null);

  // Display: during drag use envelopes, otherwise raw items
  const envelopes = localEnvelopes;
  const displayItems = envelopes ? envelopes.map((e) => e._item) : items;

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, displayItems.length);
  }, [displayItems.length]);

  useEffect(() => {
    return () => {
      if (cleanupRef.current) cleanupRef.current();
      removeGhost();
    };
  }, []);

  useEffect(() => {
    if (localEnvelopes !== null && dragIdxRef.current === null) {
      setLocalEnvelopes(null);
    }
  }, [items]);

  function createGhost(sourceEl, clientX, clientY) {
    const rect = sourceEl.getBoundingClientRect();
    const clone = sourceEl.cloneNode(true);

    inlineComputedStyles(sourceEl, clone);

    Object.assign(clone.style, {
      position: "fixed",
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      margin: "0",
      zIndex: "99999",
      pointerEvents: "none",
      opacity: "0.85",
      transform: "scale(1.02)",
      boxShadow: "0 8px 28px rgba(0,0,0,0.22), 0 0 0 2px rgba(108,99,255,0.5)",
      borderRadius: "6px",
      cursor: "grabbing",
      willChange: "left, top",
      transition: "none",
      outline: "none",
    });

    clone.querySelectorAll(".atomicSortHandle, button[aria-label]").forEach((el) => {
      el.style.display = "none";
    });

    document.body.appendChild(clone);
    ghostElRef.current = clone;
    clone._offsetX = clientX - rect.left;
    clone._offsetY = clientY - rect.top;
  }

  function moveGhost(clientX, clientY) {
    const ghost = ghostElRef.current;
    if (!ghost) return;
    ghost.style.left = `${clientX - ghost._offsetX}px`;
    ghost.style.top = `${clientY - ghost._offsetY}px`;
  }

  function removeGhost() {
    if (ghostElRef.current) {
      ghostElRef.current.remove();
      ghostElRef.current = null;
    }
  }

  const computeDropIndex = useCallback(
    (clientX, clientY) => {
      for (let i = 0; i < itemRefs.current.length; i++) {
        const el = itemRefs.current[i];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (direction === "horizontal") {
          if (clientX < rect.left + rect.width / 2) return i;
        } else {
          if (clientY < rect.top + rect.height / 2) return i;
        }
      }
      return Math.max(0, itemRefs.current.length - 1);
    },
    [direction]
  );

  function handlePointerDown(e, index) {
    e.stopPropagation();
    e.preventDefault();

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {}

    const itemEl = itemRefs.current[index];
    if (itemEl) createGhost(itemEl, e.clientX, e.clientY);

    const envs = wrapItems(items);
    localRef.current = envs;
    dragIdxRef.current = index;
    setLocalEnvelopes(envs);
    setDragIndex(index);

    function onMove(me) {
      me.preventDefault();
      me.stopPropagation();

      const fromIdx = dragIdxRef.current;
      if (fromIdx === null) return;

      moveGhost(me.clientX, me.clientY);

      const toIdx = computeDropIndex(me.clientX, me.clientY);
      if (toIdx === fromIdx) return;

      const current = [...localRef.current];
      const [moved] = current.splice(fromIdx, 1);
      current.splice(toIdx, 0, moved);
      localRef.current = current;
      dragIdxRef.current = toIdx;

      setLocalEnvelopes([...current]);
      setDragIndex(toIdx);
    }

    function onUp(ue) {
      ue.stopPropagation();
      document.removeEventListener("pointermove", onMove, true);
      document.removeEventListener("pointerup", onUp, true);
      cleanupRef.current = null;

      removeGhost();

      const finalEnvs = localRef.current;
      dragIdxRef.current = null;
      localRef.current = null;
      setDragIndex(null);

      if (finalEnvs) {
        onReorder?.(unwrapItems(finalEnvs));
      }
    }

    document.addEventListener("pointermove", onMove, true);
    document.addEventListener("pointerup", onUp, true);

    cleanupRef.current = () => {
      document.removeEventListener("pointermove", onMove, true);
      document.removeEventListener("pointerup", onUp, true);
      removeGhost();
    };
  }

  if (!editing) {
    return <>{items.map((item, i) => renderItem(item, i))}</>;
  }

  const isDraggingAny = dragIndex !== null;

  return (
    <>
      {displayItems.map((item, i) => {
        const isDragging = dragIndex === i;
        const isHovered = hoverIndex === i;
        const showHandle = !isDraggingAny && isHovered;
        const key = envelopes ? envelopes[i]._sortId : `item-${i}`;

        return (
          <div
            key={key}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
            onMouseEnter={() => {
              if (!isDraggingAny) setHoverIndex(i);
            }}
            onMouseLeave={() => {
              if (!isDraggingAny) setHoverIndex(null);
            }}
            style={{
              position: "relative",
              opacity: isDragging ? 0.3 : 1,
              transform: isDragging ? "scale(0.97)" : "none",
              outline: isDragging ? "2px dashed var(--color-text-accent, #6c63ff)" : "none",
              outlineOffset: "2px",
              borderRadius: "var(--radius-sm, 4px)",
              transition: isDragging ? "none" : "opacity 0.12s, transform 0.12s",
              cursor: isDragging ? "grabbing" : "default",
            }}
          >
            {/* Grip handle */}
            <div
              className="atomicSortHandle"
              onPointerDown={(e) => handlePointerDown(e, i)}
              style={{
                position: "absolute",
                top: 4,
                left: 4,
                zIndex: 20,
                cursor: isDragging ? "grabbing" : "grab",
                padding: "2px 5px",
                fontSize: "13px",
                userSelect: "none",
                touchAction: "none",
                lineHeight: 1,
                borderRadius: "4px",
                background: isDragging ? "var(--color-text-accent, #6c63ff)" : "rgba(0,0,0,0.55)",
                backdropFilter: "blur(4px)",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "#fff",
                opacity: showHandle ? 1 : 0,
                transition: "opacity 0.15s, background 0.12s",
                pointerEvents: showHandle ? "auto" : "none",
                ...handleStyle,
              }}
              title="Drag to reorder"
            >
              ⠿
            </div>
            {renderItem(item, i)}
          </div>
        );
      })}
    </>
  );
}

export default SortableList;
